import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { LuCheck, LuChevronDown, LuLoaderCircle, LuPackage, LuSave } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import { Field, INPUT_CLASS, TextArea, TextInput } from '../../../components/form/FormControls';
import { CARRIERS, SHIPMENT_STATUS, parseShipmentCost } from '../../../utils/shipments';
import {
  CARGO_DIRECTION,
  CARGO_DIRECTION_OPTIONS,
  InsufficientStockError,
  getCargoStatusOptions,
  normalizeCargoItems,
} from '../../../utils/companyShipments';
import { datesForStatus } from './useCompanyShipments';
import CargoItemsField from './CargoItemsField';
import CargoAttachmentsField from './CargoAttachmentsField';
import { DirectionTile, FORM_SECTION, SECTION_TITLE, STATUS_TONES } from './cargoUi';
import useCargoText from './useCargoText';

const emptyForm = {
  direction: CARGO_DIRECTION.OUTGOING,
  subject: '',
  content: '',
  status: SHIPMENT_STATUS.NOT_SENT,
  carrier: '',
  trackingNumber: '',
  shippedAt: '',
  expectedAt: '',
  deliveredAt: '',
  cost: '',
  partyName: '',
  partyOrg: '',
  partyPhone: '',
  address: '',
  items: [],
  attachments: [],
  note: '',
};

const SELECT_CLASS = `${INPUT_CLASS} tw-cursor-pointer tw-appearance-none tw-pr-9 [&>option]:tw-bg-white [&>option]:tw-text-slate-800 dark:[&>option]:tw-bg-[#1f232b] dark:[&>option]:tw-text-slate-100`;

const SectionHead = ({ title, hint }) => (
  <div className="tw-space-y-1">
    <h3 className={SECTION_TITLE}>{title}</h3>
    {hint && <p className="tw-text-xs tw-leading-snug tw-text-slate-500 dark:tw-text-slate-400">{hint}</p>}
  </div>
);

SectionHead.propTypes = { title: PropTypes.string.isRequired, hint: PropTypes.string };

/** Firma kargosu ekleme / düzenleme formu. Kaydetme çağıran tarafa bırakılır. */
const CargoFormModal = ({ show, onHide, shipment, initialDirection, stocks, onSave }) => {
  const { t, language, label, directionText, statusText, carrierName } = useCargoText();
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!show) return;
    setErrors({});
    setForm(
      shipment
        ? {
            ...emptyForm,
            ...shipment,
            cost: shipment.cost == null ? '' : String(shipment.cost).replace('.', ','),
          }
        : { ...emptyForm, direction: initialDirection || emptyForm.direction }
    );
  }, [show, shipment, initialDirection]);

  const direction = directionText(form.direction);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handleStatusChange = (status) =>
    setForm((prev) => ({ ...prev, status, ...datesForStatus(prev, status) }));

  const validate = () => {
    const next = {};
    if (!form.subject.trim()) next.subject = label('form.errors.subject');
    if (!form.partyName.trim()) next.partyName = label('form.errors.party', { party: direction.party });
    if (form.status !== SHIPMENT_STATUS.NOT_SENT && !form.carrier) {
      next.carrier = label('form.errors.carrier');
    }
    if (Number.isNaN(parseShipmentCost(form.cost))) {
      next.cost = label('form.errors.cost');
    }
    if (form.items.some((item) => item.stockId && !(parseFloat(item.quantity) > 0))) {
      next.items = label('form.errors.items');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (uploading) {
      toast.error(label('toast.waitUpload'));
      return;
    }
    if (!validate()) return;

    setSaving(true);
    try {
      // Yalnızca form alanları yazılır; id/history/appliedItems gibi kayıt
      // alanları kaydetme katmanında yönetilir.
      const payload = Object.keys(emptyForm).reduce(
        (acc, key) => ({ ...acc, [key]: form[key] }),
        {}
      );
      await onSave({
        ...payload,
        subject: form.subject.trim(),
        partyName: form.partyName.trim(),
        trackingNumber: form.trackingNumber.trim(),
        cost: parseShipmentCost(form.cost),
        items: normalizeCargoItems(form.items),
      });
      onHide();
    } catch (error) {
      if (error instanceof InsufficientStockError) {
        setErrors((prev) => ({ ...prev, items: error.message }));
      }
      // Bildirim çağıran tarafta gösteriliyor
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={Boolean(show)}
      onClose={() => !saving && onHide()}
      size="lg"
      title={shipment ? label('form.titleEdit') : label('form.titleNew')}
      subtitle={label('form.subtitle')}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-orange-400 tw-to-rose-500 tw-shadow-orange-500/30" size="xs">
          <LuPackage aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onHide} disabled={saving}>{label('form.cancel')}</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving || uploading} className="max-sm:tw-flex-1">
            {saving ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {saving ? label('form.saving') : shipment ? label('form.update') : label('form.save')}
          </Button>
        </>
      )}
    >
      <div className="tw-space-y-4">
        {/* Yön: gelen / giden */}
        <div role="radiogroup" aria-label={label('form.directionLabel')} className="tw-grid tw-grid-cols-1 tw-gap-2 sm:tw-grid-cols-2">
          {CARGO_DIRECTION_OPTIONS.map((option) => {
            const selected = form.direction === option.value;
            const text = directionText(option.value);
            const ring = option.value === CARGO_DIRECTION.INCOMING ? 'tw-border-emerald-500/50 tw-bg-emerald-500/[0.07]' : 'tw-border-sky-500/50 tw-bg-sky-500/[0.07]';
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setField('direction', option.value)}
                className={`tw-relative tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-p-3 tw-text-left tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
                  selected ? ring : 'tw-border-slate-200 tw-bg-white/70 hover:tw-bg-white dark:tw-border-white/10 dark:tw-bg-white/[0.03] dark:hover:tw-bg-white/[0.06]'
                }`}
              >
                <DirectionTile direction={option.value} size="lg" />
                <span className="tw-min-w-0 tw-flex-1">
                  <span className="tw-block tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{text.label}</span>
                  <span className="tw-block tw-text-xs tw-leading-snug tw-text-slate-500 dark:tw-text-slate-400">{text.description}</span>
                </span>
                <span
                  aria-hidden="true"
                  className={`tw-grid tw-h-5 tw-w-5 tw-shrink-0 tw-place-items-center tw-rounded-full tw-border ${
                    selected
                      ? `tw-border-transparent tw-text-white ${option.value === CARGO_DIRECTION.INCOMING ? 'tw-bg-emerald-500' : 'tw-bg-sky-500'}`
                      : 'tw-border-slate-300 tw-text-transparent dark:tw-border-white/25'
                  }`}
                >
                  <LuCheck className="tw-h-3 tw-w-3" strokeWidth={3} />
                </span>
              </button>
            );
          })}
        </div>

        <section className={FORM_SECTION}>
          <SectionHead title={label('form.sections.shipment')} hint={label('form.sections.shipmentHint')} />
          <Field label={label('form.subject')} htmlFor="cargoSubject" required error={errors.subject}>
            <TextInput
              id="cargoSubject"
              value={form.subject}
              onChange={(e) => setField('subject', e.target.value)}
              placeholder={label('form.subjectPlaceholder')}
              maxLength={120}
              error={errors.subject}
            />
          </Field>
          <Field label={label('form.content')} htmlFor="cargoContent">
            <TextArea
              id="cargoContent"
              rows={2}
              value={form.content}
              onChange={(e) => setField('content', e.target.value)}
              placeholder={label('form.contentPlaceholder')}
            />
          </Field>
        </section>

        <section className={FORM_SECTION}>
          <SectionHead title={label('form.sections.status')} hint={label('form.statusHint')} />
          <div role="radiogroup" aria-label={label('form.sections.status')} className="tw-grid tw-grid-cols-2 tw-gap-2 sm:tw-grid-cols-4">
            {getCargoStatusOptions(form.direction).map((option) => {
              const selected = form.status === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => handleStatusChange(option.value)}
                  className={`tw-flex tw-min-h-[2.75rem] tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-2xl tw-border tw-px-3 tw-py-2 tw-text-center tw-text-xs tw-font-semibold tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
                    selected
                      ? STATUS_TONES[option.tone]
                      : 'tw-border-slate-200 tw-bg-white/70 tw-text-slate-600 hover:tw-bg-white dark:tw-border-white/10 dark:tw-bg-white/[0.03] dark:tw-text-slate-300 dark:hover:tw-bg-white/[0.07]'
                  }`}
                >
                  {selected && <LuCheck aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />}
                  {statusText(form.direction, option.value).label}
                </button>
              );
            })}
          </div>
          <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-3">
            <Field label={label('form.carrier')} htmlFor="cargoCarrier" error={errors.carrier}>
              <span className="tw-relative tw-block">
                <select
                  id="cargoCarrier"
                  value={form.carrier}
                  onChange={(e) => setField('carrier', e.target.value)}
                  aria-invalid={errors.carrier ? true : undefined}
                  className={`${SELECT_CLASS} ${errors.carrier ? 'tw-ring-2 tw-ring-rose-500/50' : ''}`}
                >
                  <option value="">{label('form.carrierPlaceholder')}</option>
                  {CARRIERS.map((carrier) => (
                    <option key={carrier.value} value={carrier.value}>{carrierName(carrier.value)}</option>
                  ))}
                </select>
                <LuChevronDown aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-right-3 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-slate-400" />
              </span>
            </Field>
            <Field label={label('form.tracking')} htmlFor="cargoTracking">
              <TextInput
                id="cargoTracking"
                value={form.trackingNumber}
                onChange={(e) => setField('trackingNumber', e.target.value)}
                placeholder={label('form.trackingPlaceholder')}
              />
            </Field>
            <Field label={label('form.cost')} htmlFor="cargoCost" error={errors.cost}>
              <span className="tw-relative tw-block">
                <span className="tw-pointer-events-none tw-absolute tw-left-3.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-sm tw-font-semibold tw-text-slate-400">₺</span>
                <TextInput
                  id="cargoCost"
                  inputMode="decimal"
                  value={form.cost}
                  onChange={(e) => setField('cost', e.target.value)}
                  placeholder={label('form.costPlaceholder')}
                  error={errors.cost}
                  className="tw-pl-8"
                />
              </span>
            </Field>
          </div>
          <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-3">
            <Field label={label('form.shippedAt')} htmlFor="cargoShippedAt">
              <TextInput id="cargoShippedAt" type="date" value={form.shippedAt}
                onChange={(e) => setField('shippedAt', e.target.value)} />
            </Field>
            <Field label={label('form.expectedAt')} htmlFor="cargoExpectedAt" hint={label('form.expectedHint')}>
              <TextInput id="cargoExpectedAt" type="date" value={form.expectedAt}
                min={form.shippedAt || undefined}
                onChange={(e) => setField('expectedAt', e.target.value)} />
            </Field>
            <Field label={label('form.deliveredAt')} htmlFor="cargoDeliveredAt">
              <TextInput id="cargoDeliveredAt" type="date" value={form.deliveredAt}
                min={form.shippedAt || undefined}
                onChange={(e) => setField('deliveredAt', e.target.value)} />
            </Field>
          </div>
        </section>

        <section className={FORM_SECTION}>
          <SectionHead title={direction.party} hint={label('form.sections.partyHint')} />
          <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-3">
            <Field label={direction.partyName} htmlFor="cargoParty" required error={errors.partyName}>
              <TextInput
                id="cargoParty"
                value={form.partyName}
                onChange={(e) => setField('partyName', e.target.value)}
                placeholder={label('form.partyPlaceholder')}
                error={errors.partyName}
              />
            </Field>
            <Field label={label('form.partyOrg')} htmlFor="cargoPartyOrg">
              <TextInput id="cargoPartyOrg" value={form.partyOrg}
                onChange={(e) => setField('partyOrg', e.target.value)} placeholder={label('form.partyOrgPlaceholder')} />
            </Field>
            <Field label={label('form.partyPhone')} htmlFor="cargoPartyPhone">
              <TextInput id="cargoPartyPhone" type="tel" value={form.partyPhone}
                onChange={(e) => setField('partyPhone', e.target.value)} placeholder={label('form.partyPhonePlaceholder')} />
            </Field>
          </div>
          <Field label={direction.address} htmlFor="cargoAddress">
            <TextArea id="cargoAddress" rows={2} value={form.address}
              onChange={(e) => setField('address', e.target.value)} placeholder={label('form.addressPlaceholder')} />
          </Field>
        </section>

        <section className={FORM_SECTION}>
          <SectionHead title={label('form.sections.items')} />
          <CargoItemsField
            items={form.items}
            onChange={(items) => setField('items', items)}
            stocks={stocks}
            direction={form.direction}
            error={errors.items}
          />
        </section>

        <section className={FORM_SECTION}>
          <SectionHead title={label('form.sections.files')} hint={label('form.sections.filesHint')} />
          <CargoAttachmentsField
            attachments={form.attachments}
            onChange={(attachments) => setField('attachments', attachments)}
            onUploadingChange={setUploading}
          />
          <Field label={label('form.note')} htmlFor="cargoNote">
            <TextArea id="cargoNote" rows={2} value={form.note}
              onChange={(e) => setField('note', e.target.value)} placeholder={label('form.notePlaceholder')} />
          </Field>
        </section>
      </div>
    </Modal>
  );
};

CargoFormModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  shipment: PropTypes.object,
  initialDirection: PropTypes.string,
  stocks: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSave: PropTypes.func.isRequired,
};

export default CargoFormModal;
